import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Select,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Alert,
  Space,
  Spin,
  Empty
} from 'antd';
import {
  ReloadOutlined,
  MonitorOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useNetworks } from '../../hooks/useNetworkManagement';
import { useCoins } from '../../hooks/useCoinManagement';
import { useCoinNetworks } from '../../hooks/useCoinNetworkManagement';
import { useHotWalletsByNetworkAndCoin } from '../../hooks/useWalletManagement';
import { useMonitorAddress, useDetectedDeposits } from '../../hooks/useDebug';
import { formatTimestamp } from '../../utils/dateUtils';
import CopyToClipboard from '../common/CopyToClipboard';

const { Text, Paragraph } = Typography;
const { Option } = Select;

/**
 * IndexerTestTab component for testing blockchain indexers
 * Allows users to monitor an address and see deposits detected by the indexer
 */
const IndexerTestTab: React.FC = () => {
  const { t: commonT } = useTranslationWithNamespace('common');
  const { t: debugT } = useTranslationWithNamespace('debug');
  const [form] = Form.useForm();

  // State
  const [selectedNetwork, setSelectedNetwork] = useState<string>('');
  const [selectedCoin, setSelectedCoin] = useState<string>('');
  const [monitoredAddress, setMonitoredAddress] = useState<string>('');
  const [isMonitoring, setIsMonitoring] = useState<boolean>(false);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Fetch networks, coins, and coin networks
  const {
    data: networksData,
    isLoading: isLoadingNetworks,
    error: networksError
  } = useNetworks();

  const {
    data: coinsData,
    isLoading: isLoadingCoins,
    error: coinsError
  } = useCoins();

  const {
    data: coinNetworksData,
    isLoading: isLoadingCoinNetworks,
    error: coinNetworksError
  } = useCoinNetworks();

  // Fetch hot wallets for the selected network and coin
  const {
    data: hotWalletsData,
    isLoading: isLoadingHotWallets,
    error: hotWalletsError
  } = useHotWalletsByNetworkAndCoin(
    selectedNetwork,
    selectedCoin,
    !!selectedNetwork && !!selectedCoin
  );

  // Log data for debugging - match exactly how it's done in CoinNetworksManagementPage
  useEffect(() => {
    console.log('Networks data:', networksData);
    console.log('Coins data:', coinsData);
    console.log('Coin networks data:', coinNetworksData);
    console.log('Hot wallets data:', hotWalletsData);
  }, [networksData, coinsData, coinNetworksData, hotWalletsData]);

  // Custom hooks for monitoring address and fetching deposits
  const monitorAddressMutation = useMonitorAddress();
  const {
    data: depositsData,
    isLoading: isLoadingDeposits,
    error: depositsError,
    refetch: refetchDeposits
  } = useDetectedDeposits(selectedNetwork, monitoredAddress, isMonitoring);

  // Effect to update deposits when data changes
  useEffect(() => {
    if (depositsData && typeof depositsData === 'object' && 'deposits' in depositsData) {
      setDeposits(depositsData.deposits as any[]);
    }
  }, [depositsData]);

  // Effect to set up polling for deposits
  useEffect(() => {
    if (isMonitoring) {
      // Poll for deposits every 10 seconds
      const interval = setInterval(() => {
        refetchDeposits();
      }, 10000);
      setPollingInterval(interval);
    } else {
      // Clear polling interval when not monitoring
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }

    // Cleanup on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isMonitoring, refetchDeposits, pollingInterval]);

  // Handle network selection - match exactly how it's done in CoinNetworksManagementPage
  const handleNetworkChange = (value: string) => {
    console.log('Network selected:', value);
    setSelectedNetwork(value);
    form.setFieldsValue({ coin: undefined, address: '' });
    setSelectedCoin('');
    setMonitoredAddress('');
  };

  // Handle coin selection - match exactly how it's done in CoinNetworksManagementPage
  const handleCoinChange = (value: string) => {
    console.log('Coin selected:', value);
    setSelectedCoin(value);
    form.setFieldsValue({ coin: value, address: '' });
    setMonitoredAddress('');
  };

  // Handle wallet selection
  const handleWalletChange = (value: string) => {
    console.log('Wallet selected:', value);
    // We don't need to set the address field anymore since we're removing it
    // Just store the selected wallet address for monitoring
    setMonitoredAddress('');
  };

  // Start monitoring an address - follow pattern from CoinNetworksManagementPage
  const handleStartMonitoring = async () => {
    try {
      // Validate form fields
      const values = await form.validateFields();
      console.log('Form values for monitoring:', values);

      // Log the selected network and coin
      console.log('Selected network for monitoring:', selectedNetwork);
      console.log('Selected coin for monitoring:', selectedCoin);

      // Get the wallet address from the form
      const walletAddress = values.wallet;

      if (!walletAddress) {
        console.error('No wallet address selected');
        return;
      }

      // Call API to start monitoring the address
      const response = await monitorAddressMutation.mutateAsync({
        blockchain: selectedNetwork,
        address: walletAddress,
        coin: selectedCoin
      });

      console.log('Monitor address API response:', response);

      // Update state
      setMonitoredAddress(walletAddress);
      setIsMonitoring(true);
      setDeposits([]);

      // Initial fetch of deposits
      refetchDeposits();
    } catch (error) {
      console.error('Failed to start monitoring:', error);
    }
  };

  // Stop monitoring
  const handleStopMonitoring = async () => {
    try {
      // Call API to stop monitoring the address
      // This is a client-side only operation, so we don't need to call the API
      // But we'll log it for debugging purposes
      console.log('Stopping monitoring for address:', monitoredAddress);

      // Update state
      setIsMonitoring(false);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      // Reset the monitored address
      setMonitoredAddress('');
    } catch (error) {
      console.error('Failed to stop monitoring:', error);
    }
  };

  // Format networks data for selection - match exactly how it's done in CoinNetworksManagementPage
  const networks = useMemo(() => {
    console.log('Raw networksData:', networksData);

    if (!networksData) return [];

    // Handle array format directly
    if (Array.isArray(networksData)) {
      return networksData.map((network: any) => ({
        id: network._id || network.id,
        title: network.title || '',
        full_name: network.full_name || ''
      }));
    }

    // Handle object with data property (standard API response)
    if (networksData.data && Array.isArray(networksData.data)) {
      return networksData.data.map((network: any) => ({
        id: network._id || network.id,
        title: network.title || '',
        full_name: network.full_name || ''
      }));
    }

    // Handle object with networks property (some API responses)
    if (networksData.networks && Array.isArray(networksData.networks)) {
      return networksData.networks.map((network: any) => ({
        id: network._id || network.id,
        title: network.title || '',
        full_name: network.full_name || ''
      }));
    }

    // If we can't determine the format, return empty array
    console.error('Unknown network data format:', networksData);
    return [];
  }, [networksData]);

  // Format coins data for selection - match exactly how it's done in CoinNetworksManagementPage
  const coins = useMemo(() => {
    return coinsData?.map((coin: any) => ({
      id: coin._id || coin.id,
      symbol: coin.symbol || '',
      full_name: coin.full_name || ''
    })) || [];
  }, [coinsData]);

  // Get available coins for the selected network
  const availableCoins = useMemo(() => {
    if (!selectedNetwork || !coinNetworksData) return [];

    // Get coin networks data in a consistent format
    const coinNetworks = Array.isArray(coinNetworksData)
      ? coinNetworksData
      : coinNetworksData.data || [];

    console.log('Coin networks for filtering:', coinNetworks);
    console.log('All coins:', coins);
    console.log('Selected network:', selectedNetwork);

    // Filter coin networks by the selected network
    const filteredCoinNetworks = coinNetworks.filter((cn: any) => {
      const networkValue = cn.network_full_name || cn.network || '';
      return networkValue === selectedNetwork;
    });

    console.log('Filtered coin networks:', filteredCoinNetworks);

    // Get the coins for the filtered coin networks
    return filteredCoinNetworks.map((cn: any) => {
      const coinSymbol = cn.coin_symbol || cn.coin || '';
      return coins.find((c: any) => c.symbol === coinSymbol);
    }).filter(Boolean);
  }, [selectedNetwork, coinNetworksData, coins]);

  // Format hot wallets data for selection
  const hotWallets = useMemo(() => {
    if (!hotWalletsData) return [];

    // Handle different response formats
    const wallets = hotWalletsData.data?.wallets ||
                   hotWalletsData.wallets ||
                   [];

    return wallets.map((wallet: any) => ({
      id: wallet._id || wallet.id,
      address: wallet.public_address || '',
      type: wallet.wallet_type || 'hot'
    }));
  }, [hotWalletsData]);

  // Log formatted data for debugging
  useEffect(() => {
    if (coins.length > 0) {
      console.log('Formatted coins for selection:', coins);
    }
    if (networks.length > 0) {
      console.log('Formatted networks for selection:', networks);
    }
    if (hotWallets.length > 0) {
      console.log('Formatted hot wallets for selection:', hotWallets);
    }
  }, [coins, networks, hotWallets]);

  // Columns for the deposits table
  const depositsColumns = [
    {
      title: debugT('indexerTest.table.txid', 'Transaction ID'),
      dataIndex: 'txid',
      key: 'txid',
      render: (txid: string) => (
        <Space>
          <Text ellipsis style={{ maxWidth: 200 }}>{txid}</Text>
          <CopyToClipboard text={txid} />
        </Space>
      )
    },
    {
      title: debugT('indexerTest.table.amount', 'Amount'),
      dataIndex: 'amount',
      key: 'amount'
    },
    {
      title: debugT('indexerTest.table.confirmations', 'Confirmations'),
      dataIndex: 'confirmations',
      key: 'confirmations'
    },
    {
      title: debugT('indexerTest.table.timestamp', 'Timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: number) => formatTimestamp(timestamp)
    },
    {
      title: debugT('indexerTest.table.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (status === 'confirmed') {
          return <Tag color="green" icon={<CheckCircleOutlined />}>{debugT('indexerTest.status.confirmed', 'Confirmed')}</Tag>;
        }
        return <Tag color="blue" icon={<ClockCircleOutlined />}>{debugT('indexerTest.status.pending', 'Pending')}</Tag>;
      }
    }
  ];

  return (
    <div>
      {/* Show errors if any */}
      {networksError && (
        <Alert
          type="error"
          message={debugT('indexerTest.errors.networksError', 'Error loading networks')}
          description={String(networksError)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {coinsError && (
        <Alert
          type="error"
          message={debugT('indexerTest.errors.coinsError', 'Error loading coins')}
          description={String(coinsError)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {coinNetworksError && (
        <Alert
          type="error"
          message={debugT('indexerTest.errors.coinNetworksError', 'Error loading coin networks')}
          description={String(coinNetworksError)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title={debugT('indexerTest.setup.title', 'Indexer Test Setup')}>
            <Form
              form={form}
              layout="vertical"
              // Only disable the form fields, not the entire form
              // This allows the stop monitoring and refresh buttons to work
            >
              <Row gutter={16}>
                <Col xs={24} md={4}>
                  <Form.Item
                    name="network"
                    label={debugT('indexerTest.setup.network', 'Network')}
                    rules={[{ required: true, message: debugT('indexerTest.validation.network', 'Please select a network') }]}
                  >
                    <Select
                      placeholder={debugT('indexerTest.setup.selectNetwork', 'Select network')}
                      loading={isLoadingNetworks}
                      onChange={handleNetworkChange}
                      disabled={isMonitoring}
                      notFoundContent={
                        isLoadingNetworks
                          ? <Spin size="small" />
                          : networks.length === 0
                            ? debugT('indexerTest.setup.noNetworks', 'No networks available')
                            : null
                      }
                    >
                      {networks.map((network: any) => (
                        <Option key={network.id} value={network.full_name}>
                          {network.title} - {network.full_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item
                    name="coin"
                    label={debugT('indexerTest.setup.coin', 'Coin')}
                    rules={[{ required: true, message: debugT('indexerTest.validation.coin', 'Please select a coin') }]}
                  >
                    <Select
                      placeholder={debugT('indexerTest.setup.selectCoin', 'Select coin')}
                      loading={isLoadingCoins || isLoadingCoinNetworks}
                      disabled={!selectedNetwork || isMonitoring}
                      onChange={handleCoinChange}
                      notFoundContent={
                        isLoadingCoins || isLoadingCoinNetworks
                          ? <Spin size="small" />
                          : !selectedNetwork
                            ? debugT('indexerTest.setup.selectNetworkFirst', 'Select a network first')
                            : availableCoins.length === 0
                              ? debugT('indexerTest.setup.noCoinsForNetwork', 'No coins available for this network')
                              : null
                      }
                    >
                      {availableCoins.map((coin: any) => (
                        <Option key={coin.id} value={coin.symbol}>
                          {coin.symbol} - {coin.full_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="wallet"
                    label={debugT('indexerTest.setup.wallet', 'Hot Wallet Address to Monitor')}
                    rules={[{ required: true, message: debugT('indexerTest.validation.wallet', 'Please select a wallet address to monitor') }]}
                  >
                    <Select
                      placeholder={debugT('indexerTest.setup.selectWallet', 'Select hot wallet address')}
                      loading={isLoadingHotWallets}
                      disabled={!selectedCoin || isMonitoring}
                      onChange={handleWalletChange}
                      style={{ width: '100%' }}
                      dropdownMatchSelectWidth={false}
                      dropdownStyle={{ minWidth: '500px' }}
                      notFoundContent={
                        isLoadingHotWallets
                          ? <Spin size="small" />
                          : !selectedCoin
                            ? debugT('indexerTest.setup.selectCoinFirst', 'Select a coin first')
                            : hotWallets.length === 0
                              ? debugT('indexerTest.setup.noWalletsForCoin', 'No hot wallets available for this coin')
                              : null
                      }
                    >
                      {hotWallets.map((wallet: any) => (
                        <Option key={wallet.id} value={wallet.address}>
                          <Space>
                            <WalletOutlined />
                            <Text>{wallet.address}</Text>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Form.Item>
                    <Space>
                      {!isMonitoring ? (
                        <Button
                          type="primary"
                          icon={<MonitorOutlined />}
                          onClick={handleStartMonitoring}
                          loading={monitorAddressMutation.isPending}
                          disabled={!selectedNetwork || !selectedCoin || !form.getFieldValue('wallet')}
                        >
                          {debugT('indexerTest.actions.startMonitoring', 'Start Monitoring')}
                        </Button>
                      ) : (
                        <>
                          <Button
                            danger
                            icon={<StopOutlined />}
                            onClick={handleStopMonitoring}
                          >
                            {debugT('indexerTest.actions.stopMonitoring', 'Stop Monitoring')}
                          </Button>
                          <Button
                            icon={<ReloadOutlined />}
                            onClick={() => refetchDeposits()}
                            loading={isLoadingDeposits}
                          >
                            {debugT('indexerTest.actions.refresh', 'Refresh')}
                          </Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      {isMonitoring && (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                message={debugT('indexerTest.instructions.title', 'Testing Instructions')}
                description={
                  <div>
                    <Paragraph>
                      {debugT('indexerTest.instructions.send', 'Send a small amount of funds to the monitored address:')}
                    </Paragraph>
                    <Paragraph>
                      <Text strong>{monitoredAddress}</Text>
                      <CopyToClipboard text={monitoredAddress} />
                    </Paragraph>
                    <Paragraph>
                      {debugT('indexerTest.instructions.wait', 'Wait for the transaction to be detected by the indexer. The table below will update automatically.')}
                    </Paragraph>
                  </div>
                }
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                title={debugT('indexerTest.deposits.title', 'Detected Deposits')}
                extra={
                  <Text type="secondary">
                    {isLoadingDeposits ? (
                      <Spin size="small" />
                    ) : (
                      debugT('indexerTest.deposits.lastUpdated', 'Last updated: {time}', {
                        time: new Date().toLocaleTimeString()
                      })
                    )}
                  </Text>
                }
              >
                {depositsError && (
                  <Alert
                    type="error"
                    message={debugT('indexerTest.deposits.error', 'Error fetching deposits')}
                    description={String(depositsError)}
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Table
                  dataSource={deposits.map((deposit, index) => ({ ...deposit, key: index }))}
                  columns={depositsColumns}
                  loading={isLoadingDeposits}
                  pagination={false}
                  locale={{
                    emptyText: (
                      <Empty
                        description={debugT('indexerTest.deposits.noDeposits', 'No deposits detected yet')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )
                  }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default IndexerTestTab;
